import { useEffect, useRef, useState } from "react";
import { Loader2, ScanSearch, Trash2, ChevronDown, ChevronRight, FileText, Upload, Trophy } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`}>{children}</span>
);

const List = ({ title, items, accent = "text-white/70" }) => !items?.length ? null : (
  <div>
    <Mono className="text-[0.5rem] text-gold-400/80">{title}</Mono>
    <ul className="mt-1.5 space-y-1">
      {items.map((x, i) => <li key={i} className={`text-xs font-light ${accent} pl-3 border-l border-gold-500/20`}>{typeof x === "string" ? x : `${x.criterion}${x.weighting ? ` — ${x.weighting}` : ""}`}</li>)}
    </ul>
  </div>
);

function ExamCard({ e, onDelete }) {
  const [open, setOpen] = useState(false);
  const r = e.result || {};
  return (
    <div className="border border-gold-500/15 bg-navy-950/40" data-testid={`tender-exam-${e.id}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gold-500/5">
        {open ? <ChevronDown className="h-4 w-4 text-gold-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-white/30 shrink-0" />}
        <span className="text-white/80 text-sm truncate">{e.title}</span>
        {e.had_winner && <Mono className="text-[0.5rem] text-emerald-300 shrink-0">+ Winner analysed</Mono>}
        <span className="ml-auto flex items-center gap-3 shrink-0">
          <Mono className="text-[0.5rem] text-white/35">{r.buyer || ""}</Mono>
          <Mono className="text-[0.5rem] text-white/35">{e.created_at?.slice(0, 10)}</Mono>
        </span>
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-4">
          {r.summary && <p className="text-white/60 text-xs font-light border-l-2 border-gold-500/40 pl-3">{r.summary}</p>}
          <div className="grid md:grid-cols-2 gap-5">
            <List title="Evaluation criteria" items={r.evaluation_criteria} />
            <List title="Mandatory requirements" items={r.mandatory_requirements} />
            <List title="Winner key points" items={r.winner_key_points} accent="text-emerald-300/90" />
            <List title="Win themes" items={r.win_themes} accent="text-gold-300/90" />
            <List title="Gaps & risks" items={r.gaps_risks} accent="text-amber-300/80" />
            <List title="Recommended actions" items={r.recommended_actions} />
          </div>
          {(r.price_signals || r.closing_date) && (
            <div className="flex gap-6 text-xs text-white/50 font-light">
              {r.closing_date && <span><b className="text-white/70">Closing:</b> {r.closing_date}</span>}
              {r.price_signals && <span><b className="text-white/70">Price signals:</b> {r.price_signals}</span>}
            </div>
          )}
          <button onClick={() => onDelete(e.id)} data-testid={`tender-exam-del-${e.id}`} className="text-white/25 hover:text-red-400 text-xs inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> Delete analysis</button>
        </div>
      )}
    </div>
  );
}

export default function TenderExaminer() {
  const [exams, setExams] = useState([]);
  const [tenderFiles, setTenderFiles] = useState([]);
  const [winnerFiles, setWinnerFiles] = useState([]);
  const [pasted, setPasted] = useState("");
  const [busy, setBusy] = useState(false);
  const tRef = useRef(null);
  const wRef = useRef(null);
  const load = () => api.get("/tender/exams").then((r) => setExams(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const run = async () => {
    if (!tenderFiles.length && pasted.trim().length < 200) return toast.error("Upload the tender document(s) or paste the tender text.");
    setBusy(true);
    const fd = new FormData();
    tenderFiles.forEach((f) => fd.append("tender_files", f));
    winnerFiles.forEach((f) => fd.append("winner_files", f));
    fd.append("pasted_text", pasted);
    try {
      const { data } = await api.post("/tender/examine", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Examined: ${data.title}`);
      setTenderFiles([]); setWinnerFiles([]); setPasted("");
      if (tRef.current) tRef.current.value = "";
      if (wRef.current) wRef.current.value = "";
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analysis failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="border border-gold-500/20 bg-navy-900/40 p-5 space-y-4" data-testid="tender-examiner">
      <div>
        <Mono className="text-gold-400 text-[0.55rem] flex items-center gap-2"><ScanSearch className="h-3.5 w-3.5" /> Tender examiner</Mono>
        <p className="text-white/40 text-xs font-light mt-1">Drop an LGA public tender (RFT/RFQ pdf, docx or txt) and, when you have it, the winning submission or award notice — the AI extracts the evaluation criteria, weightings, what won it, and the themes our response must hit.</p>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <label className="inline-flex items-center gap-2 border border-gold-500/40 text-gold-300 px-4 py-2 text-xs hover:bg-gold-500/10 cursor-pointer">
          <FileText className="h-3.5 w-3.5" /> {tenderFiles.length ? `${tenderFiles.length} tender file(s)` : "Tender document(s)"}
          <input ref={tRef} type="file" accept=".pdf,.docx,.txt" multiple className="hidden" onChange={(e) => setTenderFiles(Array.from(e.target.files || []))} data-testid="tender-files-input" />
        </label>
        <label className="inline-flex items-center gap-2 border border-emerald-400/40 text-emerald-300 px-4 py-2 text-xs hover:bg-emerald-400/10 cursor-pointer">
          <Trophy className="h-3.5 w-3.5" /> {winnerFiles.length ? `${winnerFiles.length} winner file(s)` : "Winning submission (optional)"}
          <input ref={wRef} type="file" accept=".pdf,.docx,.txt" multiple className="hidden" onChange={(e) => setWinnerFiles(Array.from(e.target.files || []))} data-testid="winner-files-input" />
        </label>
        <button onClick={run} disabled={busy} data-testid="tender-examine-btn"
          className="inline-flex items-center gap-2 bg-gold-500 text-navy-950 font-semibold px-4 py-2 text-xs hover:bg-gold-400 disabled:opacity-50">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {busy ? "Analysing…" : "Examine"}
        </button>
      </div>
      <textarea rows={3} value={pasted} onChange={(e) => setPasted(e.target.value)} data-testid="tender-paste-input"
        placeholder="…or paste the tender text / award notice straight from the council's portal"
        className="w-full bg-navy-950 border border-gold-500/25 rounded-sm px-3 py-2 text-xs focus:outline-none focus:border-gold-500/60" />
      <div className="space-y-2">
        {exams.map((e) => <ExamCard key={e.id} e={e} onDelete={async (id) => { await api.delete(`/tender/exams/${id}`); load(); }} />)}
        {exams.length === 0 && <p className="text-white/30 text-xs font-light">No analyses yet.</p>}
      </div>
    </div>
  );
}
